import {
  AlertTriangle,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Wand2,
} from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '~/components/common/confirm-dialog';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import { Collapsible, CollapsibleContent } from '~/components/ui/collapsible';
import { usePermission } from '~/hooks/usePermission';
import { cn } from '~/utils/cn';
import { formatRelative } from '~/utils/date';

import {
  useRepairEnrichmentRun,
  useRevalidateEnrichmentRun,
  useValidationActionCapabilities,
} from '../../hooks/use-enrichment-validation';
import {
  humanizeRuleId,
  type ValidationConnectorCode,
} from '../../services/enrichment-validation.service';
import type { EnrichmentValidationSummary } from '../../types/enrichment-validation.api.types';
import { progressFigures } from '../../utils/validation-progress';

import { ValidationRunProgress } from './validation-run-progress';

interface Props {
  /** The run's root id (`pipelineWorkflowId ?? workflowId`): the summary / revalidate key and the
   *  repair `runId` alike, so both services act on the same run. */
  workflowId: string;
  /** The run's connector — the rule set every validation read and revalidate is scoped to. */
  connectorCode: ValidationConnectorCode;
  /** The run can be validated at all (not a scores-only INLINE / FILE / BATCH_UPLOAD job), which
   *  gates "Run validation" on a `NONE` summary. */
  canRunValidation: boolean;
  /** Page-owned summary (the drawer reads the same one): `undefined` while
   *  loading or disabled, `null` when the route is not served. */
  summary: EnrichmentValidationSummary | null | undefined;
  /** The summary read failed. Only shown when there is no earlier summary to keep. */
  isError: boolean;
  /** An accepted revalidate is waiting for its run: shown as RUNNING (contract §5). */
  isAwaitingRevalidation: boolean;
  /** Starts the summary's bounded post-revalidate / post-repair poll from the started run's id. */
  onStartRevalidationWatch: (startedRunId: string | null) => void;
  /** Opens the "View products" drawer. */
  onViewProducts: () => void;
  /** Start expanded — where the user already chose this batch (a drill-in view), the
   *  one-line strip would only make them click again. Default collapsed (page headers). */
  defaultExpanded?: boolean;
}

/** Status pill tones — the cost strip's chip, on design tokens (colour only for real states). */
const PILL_TONE_CLASS = {
  attention: 'bg-warning-50 text-warning',
  success: 'bg-success-50 text-success',
  info: 'bg-primary/10 text-primary',
  muted: 'bg-muted text-muted-foreground',
} as const;

/** Leading icon colour per pill tone. */
const ICON_TONE_CLASS = {
  attention: 'text-warning',
  success: 'text-success',
  info: 'text-primary',
  muted: 'text-muted-foreground',
} as const;

/** A state's status: the right-hand pill word (not repeated by the summary), its tone and the
 *  leading icon. */
interface Pill {
  label: string;
  tone: keyof typeof PILL_TONE_CLASS;
  icon: ReactNode;
}

/** How many `topRules` the "Top problems" row shows. */
const TOP_PROBLEMS_LIMIT = 4;

/** The run-health bar's segments, in bar order. `fixable` = needs normalising (code fixes it);
 *  `outdated` = failing under an older rule set (re-enrich fates), until the run is re-validated. */
type HealthSegmentId = 'passed' | 'fixable' | 'outdated';

/** One semantic token per segment (bar fill + legend dot). */
const SEGMENT_CLASS: Record<HealthSegmentId, string> = {
  passed: 'bg-success',
  fixable: 'bg-primary',
  outdated: 'bg-muted-foreground/50',
};

/** Invalid products the normaliser rules never produce: the re-enrich fates of verdicts reached
 *  under an older rule set (`invalid = fixableInvalid + these`, contract §3). */
function outdatedInvalid(summary: EnrichmentValidationSummary): number {
  return summary.reenrichableProducts + summary.exhaustedProducts + summary.terminalProducts;
}

/** A segment's legend `count` and its bar share (`width`, in products). */
interface HealthSegment {
  id: HealthSegmentId;
  count: number;
  width: number;
}

/** Splits `totalProducts` into the bar's segments (contract §2 / §3; verdicts are on the STORED
 *  row): `invalid = fixableInvalid + outdated` exactly, so `passed = total − invalid`. Widths sum
 *  to `total`. */
function healthSegments(summary: EnrichmentValidationSummary): HealthSegment[] {
  const passed = Math.max(0, summary.totalProducts - summary.invalidProducts);
  const outdated = outdatedInvalid(summary);
  return [
    { id: 'passed', count: passed, width: passed },
    {
      id: 'fixable',
      count: summary.fixableInvalidProducts,
      width: summary.fixableInvalidProducts,
    },
    { id: 'outdated', count: outdated, width: outdated },
  ];
}

/** Segmented run-health bar (proportional to `totalProducts`, each non-zero segment ≥ 2px) with a
 *  count + label legend. The bar is one `role="img"` whose label carries every count, so the
 *  visual-only legend is hidden from assistive tech rather than read twice. */
function HealthBar({
  summary,
  label,
  isPartial,
}: {
  summary: EnrichmentValidationSummary;
  label: string;
  isPartial: boolean;
}) {
  const { t } = useTranslation('catalog-operations');
  const total = summary.totalProducts;
  const totalParams = { count: total, formatted: total.toLocaleString() };
  const totalLabel = isPartial
    ? t('enrichmentValidation.banner.health.totalPartial', totalParams)
    : t('enrichmentValidation.banner.health.total', totalParams);
  const segments = healthSegments(summary);
  const shown = segments.filter((s) => s.count > 0);
  const segmentLabel = (id: HealthSegmentId) =>
    t(`enrichmentValidation.banner.health.segments.${id}`);
  const segmentCount = (s: HealthSegment) => s.count.toLocaleString();
  const warnings = summary.warningProducts;
  const ariaLabel = t('enrichmentValidation.banner.health.aria', {
    total: totalLabel,
    parts: shown
      .map((s) =>
        t('enrichmentValidation.banner.health.ariaPart', {
          formatted: segmentCount(s),
          label: segmentLabel(s.id),
        }),
      )
      .join(', '),
  });
  return (
    <div data-testid="batch-detail-validation-health-bar" className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className="text-foreground font-medium">{label}</span>
        <span
          data-testid="batch-detail-validation-health-total"
          className="text-muted-foreground shrink-0 tabular-nums"
        >
          {totalLabel}
        </span>
      </div>
      <div
        role="img"
        aria-label={ariaLabel}
        className="bg-muted flex h-2 w-full gap-px overflow-hidden rounded-full"
      >
        {segments
          .filter((s) => s.width > 0)
          .map((s) => (
            <span
              key={s.id}
              className={cn('h-full', SEGMENT_CLASS[s.id])}
              style={{ flexGrow: s.width, flexBasis: 0, minWidth: '2px' }}
            />
          ))}
      </div>
      <ul aria-hidden="true" className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {shown.map((s) => (
          <li
            key={s.id}
            data-testid={`batch-detail-validation-health-segment-${s.id}`}
            className="flex items-center gap-1.5"
          >
            <span className={cn('h-2 w-2 shrink-0 rounded-full', SEGMENT_CLASS[s.id])} />
            <span
              data-testid={`batch-detail-validation-health-segment-value-${s.id}`}
              className="text-foreground font-medium tabular-nums"
            >
              {segmentCount(s)}
            </span>
            <span className="text-muted-foreground">{segmentLabel(s.id)}</span>
          </li>
        ))}
        {warnings > 0 ? (
          <li
            data-testid="batch-detail-validation-health-warnings"
            className="text-muted-foreground tabular-nums"
          >
            {t('enrichmentValidation.banner.health.warnings', {
              count: warnings,
              formatted: warnings.toLocaleString(),
            })}
          </li>
        ) : null}
      </ul>
    </div>
  );
}

/** The next-step card ("Needs normalising"): count, a one-line why, cost chips and its action.
 *  Primary styling when it is the recommended step. */
function ActionCard({
  icon,
  title,
  count,
  body,
  extra = null,
  chips,
  action,
  isRecommended,
  testId,
}: {
  icon: ReactNode;
  title: string;
  count: number;
  body: string;
  /** A muted secondary line under the body. */
  extra?: string | null;
  chips: string[];
  action: ReactNode;
  isRecommended: boolean;
  /** Automation hook for the card root; child ids derive from it. */
  testId: string;
}) {
  const { t } = useTranslation('catalog-operations');
  const titleId = useId();
  return (
    <section
      aria-labelledby={titleId}
      data-testid={testId}
      className={cn(
        'flex min-w-0 flex-col gap-2 rounded-lg border p-3',
        isRecommended ? 'border-primary/40 bg-primary/5' : 'bg-muted/30',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="flex shrink-0">{icon}</span>
          <h4 id={titleId} className="text-foreground text-sm font-semibold">
            {title}
          </h4>
          {isRecommended ? (
            <span
              data-testid={`${testId}-recommended-badge`}
              className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-xs font-medium"
            >
              {t('enrichmentValidation.banner.cards.recommended')}
            </span>
          ) : null}
        </div>
        <span
          data-testid={`${testId}-value`}
          className="text-foreground shrink-0 text-lg leading-6 font-semibold tabular-nums"
        >
          {count.toLocaleString()}
          <span className="sr-only">
            {' '}
            {t('enrichmentValidation.banner.cards.countUnit', { count })}
          </span>
        </span>
      </div>
      <p className="text-muted-foreground text-sm leading-5">{body}</p>
      {extra ? <p className="text-muted-foreground text-xs leading-4">{extra}</p> : null}
      {chips.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <li
              key={chip}
              className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs"
            >
              {chip}
            </li>
          ))}
        </ul>
      ) : null}
      {action ? <div className="mt-auto pt-1">{action}</div> : null}
    </section>
  );
}

/** "Top problems": the most frequent CRITICAL rules as label + count chips. Non-interactive — the
 *  drawer cannot be opened pre-filtered to a rule yet (it takes no initial filter). */
function TopProblems({ summary }: { summary: EnrichmentValidationSummary }) {
  const { t } = useTranslation('catalog-operations');
  const rules = summary.topRules.slice(0, TOP_PROBLEMS_LIMIT);
  if (rules.length === 0) return null;
  return (
    <div
      data-testid="batch-detail-validation-top-problems"
      className="flex flex-wrap items-center gap-x-2 gap-y-1.5"
    >
      <span className="text-muted-foreground text-xs font-medium">
        {t('enrichmentValidation.banner.topProblems.label')}
      </span>
      <ul className="flex flex-wrap gap-1.5">
        {rules.map(({ rule, count }) => (
          <li
            key={rule}
            data-testid={`batch-detail-validation-top-problem-item-${rule}`}
            className="bg-background text-foreground flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs"
          >
            {/* `nsSeparator: false` keeps a `:` in a rule id part of the key (as in the drawer); a
                retired id an older verdict still carries falls back to the humanised id. */}
            <span>
              {t(`enrichmentValidation.rules.${rule}`, {
                defaultValue: humanizeRuleId(rule),
                nsSeparator: false,
              })}
            </span>
            <span className="text-muted-foreground font-medium tabular-nums">
              {count.toLocaleString()}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Collapsed-row summary text: normal weight, its parts joined by muted `·` separators. */
function StripSummary({ parts }: { parts: string[] }) {
  return (
    <>
      {parts.map((part) => (
        <span key={part} className="flex min-w-0 items-center gap-x-2">
          <span aria-hidden="true" className="text-muted-foreground">
            ·
          </span>
          <span className="text-foreground truncate font-normal">{part}</span>
        </span>
      ))}
    </>
  );
}

/** One-line collapsible strip in the Cost estimate strip's style: icon + "Validation" + summary on
 *  the left; muted meta, the status pill, Review (collapsed) and the chevron on the right. The
 *  expanded body is a panel (bar, cards, problems …) above a footer row. */
function ValidationStrip({
  summary,
  pill,
  meta,
  live = false,
  isExpanded,
  onToggle,
  onReview,
  footerStart,
  footerEnd,
  children,
}: {
  summary: string[];
  pill: Pill;
  /** Muted header meta, e.g. "Last checked 9 minutes ago". */
  meta?: string | null;
  live?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  /** Set when there is something to act on: a compact "Review" (opens the drawer) while collapsed. */
  onReview?: (() => void) | undefined;
  /** Footer, left: "View all products". */
  footerStart?: ReactNode;
  /** Footer, right: the quiet Re-run validation action. */
  footerEnd?: ReactNode;
  children?: ReactNode;
}) {
  const { t } = useTranslation('catalog-operations');
  const bodyId = useId();
  const hasFooter = Boolean(footerStart) || Boolean(footerEnd);
  return (
    <Card
      {...(live ? { role: 'status' } : {})}
      data-testid="batch-detail-validation-banner"
      className={cn(
        'flex w-full flex-col transition-[padding] duration-200',
        isExpanded ? 'px-5 py-3' : 'px-5 py-2',
      )}
    >
      {/* The header button's `after:` overlay stretches its hit area over the whole row (pill, meta
          and chevron included); the Review button sits above it (`z-10`), so nothing is nested. */}
      <div className="relative flex min-h-7 items-center gap-2">
        <h3 className="flex min-w-0 flex-1">
          <Button
            data-testid="batch-detail-validation-toggle-button"
            variant="ghost"
            aria-expanded={isExpanded}
            aria-controls={bodyId}
            onClick={onToggle}
            className="focus-visible:after:ring-ring h-7 min-w-0 flex-1 cursor-pointer justify-start gap-2 rounded-md px-0 after:absolute after:inset-0 after:rounded-md hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:after:ring-2"
          >
            <span className={cn('flex shrink-0', ICON_TONE_CLASS[pill.tone])}>{pill.icon}</span>
            <span className="text-foreground shrink-0 text-sm font-semibold">
              {t('enrichmentValidation.banner.strip.label')}
            </span>
            <span
              data-testid="batch-detail-validation-summary"
              className="flex min-w-0 items-center gap-x-2 text-sm tabular-nums"
            >
              <StripSummary parts={summary} />
            </span>
          </Button>
        </h3>
        {meta ? (
          <span
            data-testid="batch-detail-validation-last-checked"
            className="text-muted-foreground hidden shrink-0 text-xs md:inline"
          >
            {meta}
          </span>
        ) : null}
        <span
          data-testid="batch-detail-validation-status-badge"
          className={cn(
            'hidden shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium sm:inline',
            PILL_TONE_CLASS[pill.tone],
          )}
        >
          {pill.label}
        </span>
        {onReview && !isExpanded ? (
          <Button
            data-testid="batch-detail-validation-review-button"
            size="sm"
            className="relative z-10 h-7 shrink-0 cursor-pointer px-3 text-xs"
            onClick={onReview}
          >
            {t('enrichmentValidation.banner.strip.review')}
          </Button>
        ) : null}
        <span className="text-muted-foreground flex h-7 w-7 shrink-0 items-center justify-center">
          <ChevronDown
            className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')}
            aria-hidden="true"
          />
        </span>
      </div>

      {/* Body — Radix animates its height, like the cost strip's; a divided panel below the row. */}
      <Collapsible open={isExpanded}>
        <CollapsibleContent id={bodyId} data-testid="batch-detail-validation-body">
          <div className="mt-3 flex flex-col gap-4 border-t pt-4 pb-1">
            {children}
            {/* Meta is hidden in the header below `md`, so it is repeated here at those widths. */}
            {meta ? <p className="text-muted-foreground text-xs md:hidden">{meta}</p> : null}
            {hasFooter ? (
              <div className="flex flex-wrap items-center gap-2">
                {footerStart}
                {footerEnd ? <div className="ml-auto">{footerEnd}</div> : null}
              </div>
            ) : null}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

/** One muted line of strip body copy. */
function BannerLine({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <p id={id} className="text-muted-foreground text-sm leading-5">
      {children}
    </p>
  );
}

/** A confirm the banner can open, tied to the `validationRunId` it was opened for. */
type ConfirmAction = 'rerun' | 'repair';

/** Post-enrichment validation strip above the batch tabs, one state per summary `status` +
 *  `coverage` (see docs/enrichment-validation-banner.md); its actions need `data_quality:update`. */
export function EnrichmentValidationBanner({
  workflowId,
  connectorCode,
  canRunValidation,
  summary,
  isError,
  isAwaitingRevalidation,
  onStartRevalidationWatch,
  onViewProducts,
  defaultExpanded = false,
}: Props) {
  const { t } = useTranslation('catalog-operations');
  const canUpdate = usePermission('data_quality:update');
  // Collapsed unless the host says otherwise; the user's choice survives state changes
  // (component state only).
  const [isExpanded, setExpanded] = useState(defaultExpanded);
  const toggleExpanded = () => {
    setExpanded((v) => !v);
  };
  // The confirm that is open and the run it was opened for. Open is derived from it, so a refetch
  // that swaps the run (or unmounts the dialog's branch) can never reopen it by itself.
  const [confirmFor, setConfirmFor] = useState<{
    action: ConfirmAction;
    runId: string | null;
  } | null>(null);
  const busyNoteId = useId();
  const revalidate = useRevalidateEnrichmentRun(
    workflowId,
    connectorCode,
    onStartRevalidationWatch,
  );
  const repair = useRepairEnrichmentRun(workflowId, workflowId, onStartRevalidationWatch);
  // workflow-service's gates are only asked when the summary alone would offer Fix.
  const gates = useValidationActionCapabilities(
    workflowId,
    workflowId,
    connectorCode,
    canUpdate && summary?.status === 'COMPLETED' && summary.repairableProducts > 0,
  );

  // Captured now: a confirm runs later, and is tied to the run it was opened for.
  const baselineRunId = summary?.validationRunId ?? null;
  const isConfirmOpen = (action: ConfirmAction) =>
    confirmFor?.action === action && confirmFor.runId === baselineRunId;
  const openConfirm = (action: ConfirmAction) => {
    setConfirmFor({ action, runId: baselineRunId });
  };
  const confirmOpenChange = (action: ConfirmAction) => (open: boolean) => {
    setConfirmFor(open ? { action, runId: baselineRunId } : null);
  };

  /** "Run / Re-run validation" — the one recompute control in every state but RUNNING. Confirmed
   *  first when it would replace a COMPLETED result; quiet (ghost) unless it is the only action. */
  function rerunButton(label: string, confirmFirst: boolean, isPrimary = false) {
    return (
      <Button
        data-testid="batch-detail-validation-rerun-button"
        variant={isPrimary ? 'default' : 'ghost'}
        size="sm"
        className="cursor-pointer disabled:cursor-not-allowed"
        onClick={() => {
          if (confirmFirst) openConfirm('rerun');
          else revalidate.mutate();
        }}
        isLoading={revalidate.isPending}
      >
        {/* Hidden while loading — the Button renders its own spinner. */}
        {!revalidate.isPending && <RefreshCw className="h-4 w-4" aria-hidden="true" />}
        {label}
      </Button>
    );
  }

  /** "View all products" (footer, left) — primary only when nothing else in the panel is. */
  const viewProductsButton = (isPrimary: boolean) => (
    <Button
      data-testid="batch-detail-validation-view-products-button"
      variant={isPrimary ? 'default' : 'outline'}
      size="sm"
      className="cursor-pointer"
      onClick={onViewProducts}
    >
      {t('enrichmentValidation.banner.actions.viewAllProducts')}
    </Button>
  );

  const stripProps = { isExpanded, onToggle: toggleExpanded };
  /** The status for a state: its word (`strip.pill.*`), tone and icon. */
  const makePill = (key: string, tone: Pill['tone'], icon: ReactNode): Pill => ({
    label: t(`enrichmentValidation.banner.strip.pill.${key}`),
    tone,
    icon,
  });
  const unavailablePill = makePill(
    'unavailable',
    'muted',
    <CircleAlert className="h-4 w-4" aria-hidden="true" />,
  );

  // No summary yet: nothing while it loads (most runs show no banner, so a skeleton would only
  // shift the page); a failed read is "unavailable", never a clean run.
  if (summary === undefined) {
    if (!isError) return null;
    return (
      <ValidationStrip
        {...stripProps}
        pill={unavailablePill}
        summary={[t('enrichmentValidation.banner.strip.loadFailed')]}
      >
        <BannerLine>{t('enrichmentValidation.banner.unavailable.loadFailed')}</BannerLine>
      </ValidationStrip>
    );
  }

  if (summary === null) return null;

  // RUNNING, or optimistically RUNNING while an accepted revalidate / repair waits for its run.
  // The count is the previous verdict set's (the summary keeps it while running), when known.
  // The stored counts are a mix of finished pages and earlier verdicts mid-run, so the strip
  // shows the run's own progress; nothing recorded yet (just started) reads as getting ready.
  if (summary.status === 'RUNNING' || isAwaitingRevalidation) {
    const progress = summary.status === 'RUNNING' ? summary.progress : null;
    const figures = progress ? progressFigures(progress, summary.startedAt) : null;
    const total = progress?.expectedProducts ?? null;
    const percent = figures?.percent ?? null;
    const checkingLabel =
      figures !== null && percent !== null && total !== null
        ? t('enrichmentValidation.banner.running.titleProgress', {
            percent,
            checked: figures.checked.toLocaleString(),
            total: total.toLocaleString(),
          })
        : t('enrichmentValidation.banner.running.title');
    return (
      <ValidationStrip
        {...stripProps}
        live
        pill={makePill(
          'inProgress',
          'info',
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />,
        )}
        summary={[checkingLabel]}
      >
        <ValidationRunProgress progress={progress} startedAt={summary.startedAt} />
        <BannerLine>{t('enrichmentValidation.banner.running.subtitle')}</BannerLine>
      </ValidationStrip>
    );
  }

  // NONE: no banner, except "Run validation" on a validatable run (an older batch, or a lost
  // trigger — there is no automatic backfill). It is the only action, so it is the primary one.
  if (summary.status === 'NONE') {
    if (!canUpdate || !canRunValidation) return null;
    return (
      <ValidationStrip
        {...stripProps}
        pill={makePill('notRun', 'muted', <ShieldCheck className="h-4 w-4" aria-hidden="true" />)}
        summary={[t('enrichmentValidation.banner.strip.notValidated')]}
      >
        {/* Empty state: what validation is, then its one action. */}
        <div
          data-testid="batch-detail-validation-empty"
          className="flex flex-col items-start gap-3 rounded-lg border border-dashed p-4 sm:flex-row sm:items-center"
        >
          <span className="bg-muted text-muted-foreground flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1 space-y-0.5">
            <p className="text-foreground text-sm font-medium">
              {t('enrichmentValidation.banner.empty.title')}
            </p>
            <BannerLine>{t('enrichmentValidation.banner.empty.body')}</BannerLine>
          </div>
          {rerunButton(t('enrichmentValidation.banner.actions.runValidation'), false, true)}
        </div>
      </ValidationStrip>
    );
  }

  const invalid = summary.invalidProducts;
  const isPartial = summary.coverage === 'PARTIAL';
  const counts = {
    count: invalid,
    formatted: invalid.toLocaleString(),
    total: summary.totalProducts.toLocaleString(),
  };
  const rerunLabel = t('enrichmentValidation.banner.actions.rerunValidation');
  const repairable = summary.repairableProducts;
  const repairableFormatted = repairable.toLocaleString();
  // "Needs normalising" count (contract §2): the failing products code alone fixes; when there
  // are none but code can still repair something (older verdicts), the repairable products.
  const fixableInvalid = summary.fixableInvalidProducts;
  const fixCount = fixableInvalid > 0 ? fixableInvalid : repairable;
  // Products normalised by code already (their before / after is on record).
  const normalised = summary.normalisedProducts;
  const normalisedPart =
    normalised > 0
      ? [
          t('enrichmentValidation.banner.strip.normalised', {
            count: normalised,
            formatted: normalised.toLocaleString(),
          }),
        ]
      : [];
  // Failing under an older rule set: shown as one segment, with a note to re-run validation.
  const outdated = outdatedInvalid(summary);
  const outdatedNote =
    outdated > 0 ? (
      <p className="text-muted-foreground text-xs leading-4">
        {t('enrichmentValidation.banner.outdated', {
          count: outdated,
          formatted: outdated.toLocaleString(),
        })}
      </p>
    ) : null;
  // A check skipped on only part of the run (older rule sets: a `:partial` entry).
  const partialChecksNote = summary.checksSkipped.some((check) => check.endsWith(':partial')) ? (
    <p className="text-muted-foreground text-xs leading-4">
      {t('enrichmentValidation.banner.partialChecks')}
    </p>
  ) : null;
  const healthLabel = isPartial
    ? t('enrichmentValidation.banner.health.labelPartial')
    : t('enrichmentValidation.banner.health.label');

  if (summary.status === 'FAILED') {
    // The counts survive from earlier verdicts when `totalProducts > 0`, so
    // they stay visible — and browsable — while the latest run is unavailable.
    const hasLastResults = summary.totalProducts > 0;
    return (
      <ValidationStrip
        {...stripProps}
        pill={unavailablePill}
        summary={[
          t('enrichmentValidation.banner.strip.didNotFinish'),
          ...(hasLastResults
            ? [
                invalid > 0
                  ? t('enrichmentValidation.banner.strip.lastResults', counts)
                  : t('enrichmentValidation.banner.strip.lastResultsClean', {
                      total: counts.total,
                    }),
              ]
            : []),
        ]}
        onReview={hasLastResults && (invalid > 0 || repairable > 0) ? onViewProducts : undefined}
        footerStart={hasLastResults ? viewProductsButton(true) : null}
        // No COMPLETED result to replace, so no confirm.
        footerEnd={canUpdate ? rerunButton(rerunLabel, false) : null}
      >
        {/* `error` is a fixed, user-safe sentence from the server — rendered as text. */}
        <div className="flex items-start gap-2">
          <CircleAlert className="text-error mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <p
            data-testid="batch-detail-validation-error"
            className="text-foreground text-sm leading-5"
          >
            {summary.error ?? t('enrichmentValidation.banner.unavailable.subtitle')}
          </p>
        </div>
        {hasLastResults && (
          <>
            <HealthBar
              summary={summary}
              label={t('enrichmentValidation.banner.health.lastResultsLabel')}
              isPartial={isPartial}
            />
            <TopProblems summary={summary} />
            {outdatedNote}
            {partialChecksNote}
          </>
        )}
      </ValidationStrip>
    );
  }

  // COMPLETED. Re-run replaces this result, so it is confirmed first.
  const rerun = canUpdate ? rerunButton(rerunLabel, true) : null;

  // "Needs normalising": the card whenever code can repair products; the button only when the
  // repair gate is supported (its reason, muted, when it says why not).
  const repairGate = gates.repairAction;
  const retryGate = gates.invalidItemsRetry;
  const isRepairOffered = canUpdate && repairable > 0 && repairGate?.supported === true;

  // ONE recommended next step: Fix automatically, else View all products.
  const primary = isRepairOffered ? 'repair' : 'view';

  // One repair or item retry at a time per run (workflow-service 409s the other), so either in
  // flight disables Fix. `repairAction.inFlight` covers both; `invalidItemsRetry` an item retry
  // (one started elsewhere, e.g. a failed-items retry).
  const isRetryRunning = retryGate?.inFlight === true;
  const isActionBusy = isRetryRunning || repairGate?.inFlight === true;
  const showBusyNote = isActionBusy && isRepairOffered;
  const busyNoteProps = showBusyNote ? { 'aria-describedby': busyNoteId } : {};
  // While a fix / retry runs, the pill says so — the busy note itself is in the body.
  const busyPill: Pill | null = showBusyNote
    ? makePill(
        isRetryRunning ? 'reenriching' : 'fixing',
        'info',
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />,
      )
    : null;

  const repairButton = isRepairOffered ? (
    <Button
      data-testid="batch-detail-validation-fix-button"
      variant={primary === 'repair' ? 'default' : 'outline'}
      size="sm"
      className="cursor-pointer disabled:cursor-not-allowed"
      onClick={() => {
        openConfirm('repair');
      }}
      isLoading={repair.isPending}
      // Explicit `disabled` overrides the Button's `isLoading` default: list both.
      disabled={repair.isPending || isActionBusy}
      {...busyNoteProps}
    >
      {!repair.isPending && <Wand2 className="h-4 w-4" aria-hidden="true" />}
      {t('enrichmentValidation.banner.actions.fixAutomatically')}
    </Button>
  ) : null;

  /** A gate's "why not" sentence (workflow-service, rendered as text) in place of a hidden button. */
  const unsupportedNote = (reason: string | null | undefined) =>
    reason ? <p className="text-muted-foreground text-xs leading-4">{reason}</p> : null;

  const repairCard =
    repairable > 0 ? (
      <ActionCard
        testId="batch-detail-validation-fixable-card"
        icon={<Wand2 className="text-primary h-4 w-4" aria-hidden="true" />}
        title={t('enrichmentValidation.banner.cards.fixable.title')}
        count={fixCount}
        body={
          fixableInvalid > 0
            ? t('enrichmentValidation.banner.cards.fixable.bodyResolves')
            : t('enrichmentValidation.banner.cards.fixable.body')
        }
        // `repairable ⊇ fixableInvalid`, so the difference is exact: the other products the same
        // fix also normalises (older verdicts that also failed on something else).
        extra={
          fixableInvalid > 0 && repairable > fixableInvalid
            ? t('enrichmentValidation.banner.cards.fixable.alsoTidies', {
                count: repairable - fixableInvalid,
                formatted: (repairable - fixableInvalid).toLocaleString(),
              })
            : null
        }
        chips={[
          t('enrichmentValidation.banner.cards.fixable.chipCost'),
          t('enrichmentValidation.banner.cards.fixable.chipSpeed'),
        ]}
        action={
          repairButton ??
          (canUpdate && repairGate?.supported === false
            ? unsupportedNote(repairGate.unsupportedReason)
            : null)
        }
        isRecommended={primary === 'repair'}
      />
    ) : null;

  // Why Fix is disabled: an item retry, else a repair, is still running.
  const busyNote = showBusyNote ? (
    <p
      id={busyNoteId}
      role="status"
      data-testid="batch-detail-validation-busy-note"
      className="text-muted-foreground text-sm leading-5"
    >
      {isRetryRunning
        ? t('enrichmentValidation.banner.invalid.reenrichInFlight')
        : t('enrichmentValidation.banner.repairable.inFlight')}
    </p>
  ) : null;

  const partialNote = isPartial ? (
    <BannerLine>{t('enrichmentValidation.banner.partial.note')}</BannerLine>
  ) : null;

  const lastChecked = summary.lastValidatedAt
    ? t('enrichmentValidation.banner.invalid.lastChecked', {
        relative: formatRelative(summary.lastValidatedAt),
      })
    : null;

  // Every COMPLETED confirm, mounted with whichever COMPLETED strip renders below.
  const dialogs = (
    <>
      {canUpdate && (
        <ConfirmDialog
          open={isConfirmOpen('rerun')}
          testId="batch-detail-validation-rerun-confirm-dialog"
          onOpenChange={confirmOpenChange('rerun')}
          title={t('enrichmentValidation.revalidate.confirmTitle')}
          description={t('enrichmentValidation.revalidate.confirmDescription')}
          confirmLabel={t('enrichmentValidation.revalidate.confirmAction')}
          isPending={revalidate.isPending}
          onConfirm={() => {
            // Closed on confirm, like the page's recompute dialogs — the
            // button's own spinner tracks the call.
            setConfirmFor(null);
            revalidate.mutate();
          }}
        />
      )}
      {isRepairOffered && (
        <ConfirmDialog
          open={isConfirmOpen('repair')}
          testId="batch-detail-validation-repair-confirm-dialog"
          onOpenChange={confirmOpenChange('repair')}
          title={t('enrichmentValidation.repair.confirmTitle')}
          description={t('enrichmentValidation.repair.confirmDescription', {
            count: repairable,
            formatted: repairableFormatted,
          })}
          confirmLabel={t('enrichmentValidation.repair.confirmAction')}
          isPending={repair.isPending}
          isConfirmDisabled={isActionBusy}
          onConfirm={() => {
            setConfirmFor(null);
            repair.mutate();
          }}
        />
      )}
    </>
  );

  // No invalid product: a quiet strip, shown when it carries something — the Re-run control, a
  // PARTIAL note, the normalised count or the card. Otherwise nothing, as before.
  if (invalid === 0) {
    if (!rerun && repairable === 0 && normalised === 0) return null;
    const cleanParams = { count: summary.totalProducts, total: counts.total };
    const cleanTitle = isPartial
      ? t('enrichmentValidation.banner.partialClean.title', cleanParams)
      : t('enrichmentValidation.banner.clean.title', cleanParams);
    return (
      <>
        <ValidationStrip
          {...stripProps}
          summary={[cleanTitle, ...normalisedPart]}
          pill={
            busyPill ??
            (repairable > 0
              ? makePill('optionalFix', 'info', <Wand2 className="h-4 w-4" aria-hidden="true" />)
              : makePill(
                  isPartial ? 'partial' : 'ready',
                  isPartial ? 'muted' : 'success',
                  <CircleCheck className="h-4 w-4" aria-hidden="true" />,
                ))
          }
          meta={lastChecked}
          onReview={repairable > 0 || normalised > 0 ? onViewProducts : undefined}
          // Always a way to the products that were checked — a clean run included: "which
          // products are in this batch, and did each pass?" has an answer even with no problem.
          footerStart={summary.totalProducts > 0 ? viewProductsButton(primary === 'view') : null}
          footerEnd={rerun}
        >
          {summary.totalProducts > 0 ? (
            <HealthBar summary={summary} label={cleanTitle} isPartial={isPartial} />
          ) : (
            <p className="text-foreground flex items-center gap-2 text-sm font-medium">
              <CircleCheck className="text-success h-4 w-4" aria-hidden="true" />
              {cleanTitle}
            </p>
          )}
          {repairCard}
          {partialNote}
          {partialChecksNote}
          {busyNote}
        </ValidationStrip>
        {dialogs}
      </>
    );
  }

  return (
    <>
      <ValidationStrip
        {...stripProps}
        summary={[
          isPartial
            ? t('enrichmentValidation.banner.strip.invalidPartial', counts)
            : t('enrichmentValidation.banner.strip.invalid', counts),
          ...normalisedPart,
        ]}
        pill={
          busyPill ??
          makePill(
            'needsAttention',
            'attention',
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />,
          )
        }
        meta={lastChecked}
        onReview={onViewProducts}
        footerStart={viewProductsButton(primary === 'view')}
        footerEnd={rerun}
      >
        <HealthBar summary={summary} label={healthLabel} isPartial={isPartial} />
        {repairCard}
        <TopProblems summary={summary} />
        {partialNote}
        {outdatedNote}
        {partialChecksNote}
        {busyNote}
      </ValidationStrip>
      {dialogs}
    </>
  );
}
